import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { bubEvents } from 'javascripts/shared/events/bub_events';
import { ChirpButton } from 'javascripts/components/chirp_button';
import { CoverImage } from 'javascripts/components/cover_image';
import { PersonSummary } from 'javascripts/components/person_summary';
import ChirpPropShapes from 'javascripts/shared/chirp_prop_shapes';

import styles from 'stylesheets/components/author_card.module';

export function AuthorCard({
  author,
  displayOrientation,
  dotMenuOptions,
  hideFollowButtons,
  linkAudiobooks,
  showViewAllButton,
  trackingProperties,
  listItemRole,
}) {
  const [errorMessage, setErrorMessage] = useState(null);

  function onCardClick() {
    bubEvents.track('Author card', 'Clicked card', {
      ...trackingProperties,
      label: 'Author id',
      value: author.id,
    });
  }

  function onCoverClick(e, audiobook) {
    e.stopPropagation();

    bubEvents.track('Author card', 'Clicked audiobook cover', {
      ...trackingProperties,
      label: 'Author id',
      value: author.id,
      audiobookId: audiobook.id,
    });
  }

  function onFollowError(action) {
    setErrorMessage(`An error occurred while ${action} ${author.name}. Please refresh the page and try again.`);
  }

  function renderViewAll() {
    if (!showViewAllButton) {
      return null;
    }

    return <ChirpButton class={styles.viewAllButton} fullWidth href={author.url} label='View All' />;
  }

  function renderAudiobooks() {
    return (
      <div
        className={classNames(
          styles.bookList,
          { [styles.vertical]: displayOrientation === 'vertical' },
          { [styles.horizontal]: displayOrientation === 'horizontal' },
        )}
      >
        {(author.summaryAudiobooks || []).map(audiobook => (
          <div className={styles.bookCover} key={audiobook.id}>
            <CoverImage
              bookAuthor={audiobook.displayAuthors}
              bookTitle={audiobook.displayTitle}
              coverUrl={audiobook.coverUrl}
              linkUrl={linkAudiobooks ? audiobook.url : null}
              onClick={linkAudiobooks ? e => onCoverClick(e, audiobook) : null}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.authorCard} data-testid='author-card' { ...(listItemRole ? { role: 'listitem' } : {}) }>
      <a href={author.url} className={styles.cardLink} onClick={onCardClick}>
        <PersonSummary
          dotMenuOptions={dotMenuOptions}
          onFollowChange={() => setErrorMessage(null)}
          onFollowError={onFollowError}
          person={author}
          showButtons={!hideFollowButtons}
          trackingCategory='Author card'
          displayOrientation={displayOrientation}
        />
        <div className={styles.error}>{errorMessage}</div>
        {!linkAudiobooks && renderAudiobooks()}
      </a>
      {linkAudiobooks && renderAudiobooks()}
      {renderViewAll()}
    </div>
  );
}

AuthorCard.propTypes = {
  author: ChirpPropShapes.author.isRequired,
  displayOrientation: PropTypes.oneOf(['horizontal', 'vertical']),
  dotMenuOptions: PropTypes.arrayOf(PropTypes.shape),
  hideFollowButtons: PropTypes.bool,
  linkAudiobooks: PropTypes.bool,
  showViewAllButton: PropTypes.bool,
  trackingProperties: PropTypes.object,
  listItemRole: PropTypes.bool,
};

AuthorCard.defaultProps = {
  displayOrientation: 'horizontal',
  hideFollowButtons: false,
  linkAudiobooks: false,
  showViewAllButton: false,
  trackingProperties: {},
  listItemRole: PropTypes.false,
};
